import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import DagslysLogo from '../assets/Dagslys_BlackBackground_NoTagline.svg';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { User, LogOut, UserCircle, ShoppingCart, FolderTree } from 'lucide-react';
import AuthDialog from './AuthDialog';

const Header = () => {
  const { getTotalItemCount } = useCart();
  const { currentUser, signOut, currentUserProfile } = useAuth();
  const { toggleCart } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const cartTotal = getTotalItemCount();
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-[#FFC400] bg-dagslys-dark" style={{ height: 'var(--header-height)' }}>
      <div className="flex h-full w-full items-center justify-between px-4">
        <div className="flex items-center">
          <Link to="/" className="flex items-center">
            <img src={DagslysLogo} alt="Dagslys" className="h-12 w-auto" />
          </Link>
        </div>
        
        <nav className="flex items-center gap-5">
          <Link 
            to="/" 
            className="text-base font-medium text-white transition-colors hover:text-primary px-4"
          >
            Equipment
          </Link>
          {currentUser && currentUserProfile?.approvalStatus === 'approved' && (
            <>
              <Link 
                to="/checkout" 
                className="text-base font-medium text-white transition-colors hover:text-primary px-4"
              >
                Check out
              </Link>
              {location.pathname === '/' && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative text-white hover:text-primary"
                  onClick={toggleCart}
                >
                  <ShoppingCart className="h-5 w-5" />
                  {cartTotal > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {cartTotal}
                    </span>
                  )}
                </Button>
              )}
            </>
          )}

          {currentUser ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:text-primary">
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <UserCircle className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/orders')}>
                  <FolderTree className="mr-2 h-4 w-4" />
                  <span>Orders</span>
                </DropdownMenuItem>
                {currentUserProfile?.role === 'admin' && (
                  <DropdownMenuItem onClick={() => navigate('/admin')}>
                    <UserCircle className="mr-2 h-4 w-4" />
                    <span>Admin Dashboard</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign Out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button 
              variant="primary" 
              onClick={() => setIsAuthDialogOpen(true)}
              className="text-white"
            >
              Sign In
            </Button>
          )}
        </nav>
      </div>
      <AuthDialog open={isAuthDialogOpen} onOpenChange={setIsAuthDialogOpen} />
    </header>
  );
};

export default Header; 